import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  AsyncSubject,
  BehaviorSubject,
  first,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  skipWhile,
  Subscription,
  tap,
} from 'rxjs';

import { ConfigService } from '../config-service/config.service';
import { quoteRequest } from './market-place';
import { REQUEST_STEPS } from 'src/app/routes/search-inventory-component/request-quote-component/enum-request-steps';
import { CarOptionObj } from 'src/app/models/filter-car-type-option.model';
import { OptionModel } from 'src/app/models/railcar-display-options.model';
import {
  AggDetailsRes,
  RailCar,
  RailCarEndpointRes,
} from 'src/app/models/railcar.model';
import { StccsOption } from 'src/app/models/stcc-option.model';
import { ActiveFilterState } from 'src/app/models/active-filters.model';
import { LoginService } from '../login-service/login.service';

export enum CAR_TYPE {
  COVERED_HOPPER = 'covered-hopper',
  PRESSURE_TANK = 'pressure-tank',
  GENERAL_SERVICE = 'general-service',
  GONDOLA = 'gondola',
  OPEN_HOPPER = 'open-hopper',
}

const defaultFilterState: ActiveFilterState = {
  coils: 'All',
  state: 'All',
  country: 'All',
  capacity: [0, 0],
  compartments: 'All',
  car_type: '',
  pressure: 'All',
  gate_size: 'All',
  gate_type: 'All',
  insulation: 'All',
  hatch_cover: 'All',
  capacity_uom: '',
  stencil_spec: 'All',
  gross_rail_load: 'All',
  description_stcc: '',
};

@Injectable({
  providedIn: 'root',
})
export class MarketPlaceQuoteRequestService {
  private quoteRequestSource$ = new BehaviorSubject<any>(quoteRequest);
  quoteRequest$ = this.quoteRequestSource$.asObservable();

  private nextStepSource$ = new BehaviorSubject<REQUEST_STEPS>(
    REQUEST_STEPS.SELECTION
  );
  nextStep$ = this.nextStepSource$.asObservable();

  private selectedCarTypeSource$ = new BehaviorSubject<string>('');
  selectedCarType$ = this.selectedCarTypeSource$.asObservable();

  private stccSource$ = new BehaviorSubject<StccsOption | undefined>(undefined);
  stccRequest$ = this.stccSource$.asObservable();

  private productNameSource$ = new BehaviorSubject<string>('');
  productName$ = this.productNameSource$.asObservable();

  private selectedCarTypeAttributesSource$ = new BehaviorSubject<OptionModel[]>(
    []
  );
  selectedCarTypeAttributes =
    this.selectedCarTypeAttributesSource$.asObservable();

  private selectedCarListSource$ = new BehaviorSubject<RailCar[]>([]);
  selectedCarList$ = this.selectedCarListSource$.asObservable();

  private alternativePhoneSource$ = new BehaviorSubject<any>([]);
  alternativePhone$ = this.alternativePhoneSource$.asObservable();

  private carTypeOptionsSource$ = new AsyncSubject<CarOptionObj>();
  carTypeOptions$ = this.carTypeOptionsSource$.asObservable();

  private carListSource$ = new AsyncSubject<{
    [x: string]: RailCarEndpointRes;
  }>();
  carList$ = this.carListSource$.asObservable();

  private rawCarListSource$ = new AsyncSubject<RailCarEndpointRes>();
  rawCarList$ = this.rawCarListSource$.asObservable();

  private stccListSource$ = new AsyncSubject<StccsOption[]>();
  stccList$ = this.stccListSource$.asObservable();

  private newbuildsCarListSource$ = new AsyncSubject<{
    [x: string]: RailCarEndpointRes;
  }>();
  newbuildsCarList$ = this.newbuildsCarListSource$.asObservable();

  newbuildsRawCarListSource$ = new AsyncSubject<RailCarEndpointRes>();
  newbuildsRawCarList$ = this.newbuildsRawCarListSource$.asObservable();

  private newbuildsStccListSource$ = new AsyncSubject<StccsOption[]>();
  newbuildsStccList$ = this.newbuildsStccListSource$.asObservable();

  private activeFilterSource$ = new BehaviorSubject<ActiveFilterState>(
    defaultFilterState
  );
  activeFilter$ = this.activeFilterSource$.asObservable();

  private config;
  private loadingInitCars = false;

  loggedinSubscription$: Subscription | null;

  constructor(
    public configService: ConfigService,
    public httpClient: HttpClient,
    public loginService: LoginService
  ) {
    const strConfig = localStorage.getItem('config-service');
    if (strConfig) this.config = JSON.parse(strConfig);
    this.loggedinSubscription$ = this.loginService.user$.subscribe((res) => {
      if (res === null) return;

      this.getData();
      if (this.loggedinSubscription$) {
        this.loggedinSubscription$.unsubscribe();
        this.loggedinSubscription$ = null; // Clear the reference after unsubscribing
      }
    });
  }

  resFn(res: any) {
    return Object.values((res as any[])[0])[0];
  }

  async getData() {
    const { searchOptionsURL, stccCarTypesURL } = this.config
      ? this.config
      : await firstValueFrom(
          this.configService._config_$.pipe(skipWhile((x) => !x))
        );

    const token = this.loginService.getSessionToken();
    if (token === null) this.loginService.signout();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const stccModels$: Observable<any> = this.httpClient.post(
      stccCarTypesURL,
      {},
      {
        headers: headers,
      }
    );
    stccModels$.pipe(first()).subscribe((stccList) => {
      this.stccListSource$.next(stccList);
      this.stccListSource$.complete();
    });

    const carTypes$: Observable<any> = this.httpClient.post(
      searchOptionsURL,
      {},
      {
        headers: headers,
      }
    );
    const carTypes = (await firstValueFrom(
      carTypes$.pipe(map(this.resFn))
    )) as CarOptionObj;
    this.carTypeOptionsSource$.next(carTypes);
    this.carTypeOptionsSource$.complete();

    const { railCars, carDetails } = await this.getCarList();
    this.rawCarListSource$.next({ railCars, carDetails });
    this.rawCarListSource$.complete();
    this.partitionCarList(carTypes, railCars, carDetails);

    // Load new builds specific data
    // await this.getNewBuildsData();
  }

  async getNewBuildsData() {
    const { newBuildSearchOptionsURL, newBuildStccCarTypesURL } = this.config
      ? this.config
      : await firstValueFrom(
          this.configService._config_$.pipe(skipWhile((x) => !x))
        );

    const token = this.loginService.getSessionToken();
    if (token === null) this.loginService.signout();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    // New Builds STCC Models
    const newbuildsStccModels$: Observable<any> = this.httpClient.post(
      newBuildStccCarTypesURL,
      {},
      { headers }
    );
    newbuildsStccModels$.pipe(first()).subscribe((stccList) => {
      this.newbuildsStccListSource$.next(stccList);
      this.newbuildsStccListSource$.complete();
    });

    // New Builds Car Types
    const newbuildsCarTypes$: Observable<any> = this.httpClient.post(
      newBuildSearchOptionsURL,
      {},
      { headers }
    );
    const newbuildsCarTypes = (await firstValueFrom(
      newbuildsCarTypes$.pipe(map(this.resFn))
    )) as CarOptionObj;
    this.carTypeOptionsSource$.next(newbuildsCarTypes);
    this.carTypeOptionsSource$.complete();

    // Load New Builds Car List
    const { railCars, carDetails } = await this.getNewBuildsCarList(headers);
    this.newbuildsRawCarListSource$.next({ railCars, carDetails });
    this.newbuildsRawCarListSource$.complete();
    this.partitionCarList(newbuildsCarTypes, railCars, carDetails, true);
  }

  async getNewBuildsCarList(headers: HttpHeaders): Promise<RailCarEndpointRes> {
    const { newBuildsAggregationDataURL, newBuildsAggregatedDetailsURL } = this
      .config
      ? this.config
      : await firstValueFrom(
          this.configService._config_$.pipe(skipWhile((x) => !x))
        );

    const urlArr = [
      newBuildsAggregationDataURL,
      newBuildsAggregatedDetailsURL,
    ].map((url) =>
      this.httpClient.post(url, {}, { headers }).pipe(map(this.resFn))
    );

    const res = await firstValueFrom(
      forkJoin(urlArr).pipe(tap(() => (this.loadingInitCars = true)))
    )
      .then((res) => ({
        railCars: res[0] as RailCar[],
        carDetails: res[1] as AggDetailsRes,
      }))
      .finally(() => (this.loadingInitCars = false));

    return res;
  }

  async getCarList(): Promise<RailCarEndpointRes> {
    if (this.loadingInitCars) return await firstValueFrom(this.rawCarList$);

    const { aggregationDataURL, aggregatedDetailsURL } = this.config
      ? this.config
      : await firstValueFrom(
          this.configService._config_$.pipe(skipWhile((x) => !x))
        );

    const token = this.loginService.getSessionToken();
    if (token === null) this.loginService.signout();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const urlArr = [aggregationDataURL, aggregatedDetailsURL].map((x) =>
      this.httpClient.post(x, {}, { headers: headers }).pipe(map(this.resFn))
    );
    const res = await firstValueFrom(
      forkJoin(urlArr).pipe(tap(() => (this.loadingInitCars = true)))
    )
      .then((res) => ({
        railCars: res[0] as RailCar[],
        carDetails: res[1] as AggDetailsRes,
      }))
      .finally(() => (this.loadingInitCars = false));
    return res;
  }

  partitionCarList(
    carTypes: CarOptionObj,
    carList: RailCar[],
    carDetails: AggDetailsRes,
    isNewBuild = false
  ): void {
    const init = Object.keys(carTypes).reduce(
      (res, type) => ({ ...res, [type]: { railCars: [], carDetails } }),
      {} as { [x: string]: RailCarEndpointRes }
    );
    const carTypeEntries = Object.entries(carTypes);

    // Reduce carList based on the carType model_ids
    const reduced = carList.reduce((res, car) => {
      carTypeEntries.forEach((carType) => {
        if (!carType[1]?.model_ids?.includes(car?.model_id)) return;
        if (!res[carType[0]]) res[carType[0]] = { railCars: [], carDetails };
        res[carType[0]].railCars.push(car);
      });
      return res;
    }, init);

    if (isNewBuild) {
      // Update only the newbuildsCarListSource$ for new builds
      this.newbuildsCarListSource$.next(reduced);
      this.newbuildsCarListSource$.complete();
    } else {
      // Update only the carListSource$ for regular cars
      this.carListSource$.next(reduced);
      this.carListSource$.complete();
    }
  }

  setDefaultStcc(stcc: StccsOption | undefined) {
    this.stccSource$.next(stcc);
  }

  getDefaultStcc() {
    return this.stccSource$.value;
  }

  getSelectedCarList(): {
    cardAttrList: OptionModel[];
    selectedCars: RailCar[];
  } {
    const cardAttrList = this.selectedCarTypeAttributesSource$.value;
    const selectedCars = this.selectedCarListSource$.value;
    return { cardAttrList, selectedCars };
  }

  setSelectedCarList(list: RailCar[], attr: OptionModel[]) {
    this.selectedCarListSource$.next(list);
    this.selectedCarTypeAttributesSource$.next(attr);
  }

  gettingCarList(): boolean {
    return this.loadingInitCars;
  }

  setProductName(name: string) {
    this.productNameSource$.next(name);
  }

  getProductName() {
    return this.productNameSource$.value;
  }

  setActiveFilter(value: ActiveFilterState): void {
    this.activeFilterSource$.next(value);
  }

  getActiveFilter(): ActiveFilterState {
    return this.activeFilterSource$.value;
  }

  getSelectedCarType() {
    return this.selectedCarTypeSource$.value;
  }

  isValidStep(step: REQUEST_STEPS) {
    return this.quoteRequestSource$.value[step].isValid;
  }

  setQuoteRequest(request: any) {
    this.quoteRequestSource$.next(request);
  }

  setNextStep(step: REQUEST_STEPS) {
    this.nextStepSource$.next(step);
    window.scroll(0, 0);
  }

  getCurrenStep() {
    return this.nextStepSource$.value;
  }

  setAlternativePhone(phone: any) {
    this.alternativePhoneSource$.next(phone);
  }

  getAlternativePhone() {
    return this.alternativePhoneSource$.value;
  }

  selectCarType(carType: CAR_TYPE | '') {
    if (carType === '') {
      this.selectedCarTypeSource$.next('');
      return;
    }
    const request = { ...quoteRequest };
    this.clearValues(request);
    this.setQuoteRequest(request);
    this.selectedCarTypeSource$.next(carType);
  }

  clearValues(item: any, level = 0): any {
    for (const index in item) {
      if (Array.isArray(item[index])) {
        item[index] = '';
      } else if (typeof item[index] == 'object' && level < 2) {
        this.clearValues(item[index], level + 1);
      } else {
        if (index === 'selected' || index === 'isValid') item[index] = false;
        else if (index === 'trinSight') item[index] = true;
        else item[index] = '';
      }
    }
  }

  canNavigate(nextStep: REQUEST_STEPS) {
    const arrPages = [
      REQUEST_STEPS.SELECTION,
      REQUEST_STEPS.MECHANICAL_OPTIONS,
      REQUEST_STEPS.LEASING_OPTIONS,
      REQUEST_STEPS.REVIEW,
    ];
    const nextPageIndex = arrPages.indexOf(nextStep);

    const startIndex = 1;
    for (let i = startIndex; i < nextPageIndex; i++) {
      if (!this.isValidStep(arrPages[i])) return false;
    }
    return true;
  }
}
